import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Badge from 'components/Badge';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import {
  DashboardLayout,
  MainContent,
  Sidebar,
} from 'components/DashboardLayout';
import DataTable, { CellPrimary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import ExportModal from 'components/ExportModal';
import Toggle from 'components/FormFields/Toggle';
import { ApproveModal } from 'components/Modal';
import PageContainer from 'components/PageContainer';
import {
  HeaderContainer,
  HeaderTitle,
  HeaderSubtitle,
} from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import { WaiverNotSigned } from 'components/SVG/WaiverNotSigned';
import TableFilter from 'components/TableFilter';
import Toaster from 'components/Toaster';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import {
  useApproveMember,
  useUnApproveMember,
} from 'pages/app/AnnualTickets/Annuals/gql/useApproveMemberships';
import { useGetAccountMembers } from 'pages/app/AnnualTickets/Annuals/gql/useGetAccountMembers';
import { useGetAccountMemberships } from 'pages/app/AnnualTickets/Annuals/gql/useGetAccountMemberships';
import { useGetMembershipDivisions } from 'pages/app/AnnualTickets/Members/gql/useGetMembershipDivisions';
import { AppContext } from 'shared/AppContext';
import { formatPhoneNumber } from 'shared/formatters';
import useAnnualsExport from './hooks/useAnnualsExport';

const MemberLink = styled(Link)`
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const TrackAnnualUsers = () => {
  const isTicketHoss = 'tickethoss' === process.env.REACT_APP_PLATFORM;
  const thisYear = Number(moment().format('YYYY'));

  const { pathname } = useLocation();
  const isTrackAdmin = pathname?.includes('/admin-track');
  const isEmployeeAdmin = pathname?.includes('/admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Get year from URL or default to current year
  const urlYear = input.year ? Number(input.year) : thisYear;

  // Applied filter state - used for the main data query (only updates on Apply)
  const [appliedYear, setAppliedYear] = useState(urlYear);
  const [appliedTypeId, setAppliedTypeId] = useState(null);
  const [appliedDivision, setAppliedDivision] = useState(null);

  // Pending filter state - used for populating filter options (updates on change)
  const [pendingYear, setPendingYear] = useState(urlYear);
  const [pendingTypeId, setPendingTypeId] = useState(null);
  const [pendingDivision, setPendingDivision] = useState(null);

  const [members, setMembers] = useState([]);
  const [count, setCount] = useState(0);
  const [currentMember, setCurrentMember] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [unCheck, setUncheck] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [toasterData, setToasterData] = useState(null);

  const {
    state: { accountId, accountName },
  } = useContext(AppContext);

  // Main data query - uses APPLIED filter state (only runs when filters are applied)
  const { data, loading, refetch } = useGetAccountMembers({
    year: appliedYear,
    typeId: appliedTypeId || null,
    division: appliedDivision || null,
    accountId,
  });

  // Filter option queries - use PENDING filter state (run automatically for dynamic options)
  const { data: memberships, loading: membershipsLoading } =
    useGetAccountMemberships({
      year: pendingYear,
      accountId,
    });
  const { data: divisions, loading: divisionsLoading } =
    useGetMembershipDivisions(pendingTypeId || null);

  // Approve/Unapprove hooks
  const approveMember = useApproveMember();
  const unApproveMember = useUnApproveMember();

  // Export hook
  const { startExport, subscribeToProgress, getDownloadUrl, isExporting } =
    useAnnualsExport();

  useEffect(() => {
    if (data?.getAccountAnnuals) {
      setMembers(data.getAccountAnnuals.results || []);
      setCount(data.getAccountAnnuals.count || 0);
    }
  }, [data]);

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle approval toggle
  const handleApprovalToggle = (member) => {
    setCurrentMember(member);
    setUncheck(!!member.approved);
    setShowApproveModal(true);
  };

  // Confirm approval
  const onConfirmApprove = async () => {
    if (!currentMember) return;
    const result = unCheck
      ? await unApproveMember(currentMember.id)
      : await approveMember(currentMember.id);
    if (result) {
      refetch();
    }
    return result;
  };

  // Handle filter change - updates pending state for dynamic filter options
  const handleFilterChange = (newFilters) => {
    if (newFilters.year !== undefined) {
      const newYear = newFilters.year ? Number(newFilters.year) : thisYear;
      setPendingYear(newYear);
      // Reset type and division when year changes
      if (newYear !== pendingYear) {
        setPendingTypeId(null);
        setPendingDivision(null);
      }
    }
    if (newFilters.typeId !== undefined) {
      setPendingTypeId(newFilters.typeId || null);
      // Reset division when type changes
      if (newFilters.typeId !== pendingTypeId) {
        setPendingDivision(null);
      }
    }
    if (newFilters.division !== undefined) {
      setPendingDivision(newFilters.division || null);
    }
  };

  // Handle filter apply - updates applied state to trigger main query
  const handleApplyFilters = (newFilters) => {
    const newYear = newFilters.year ? Number(newFilters.year) : thisYear;
    const newTypeId = newFilters.typeId || null;
    const newDivision = newFilters.division || null;

    // Update applied state
    setAppliedYear(newYear);
    setAppliedTypeId(newTypeId);
    setAppliedDivision(newDivision);

    // Sync pending state with applied state
    setPendingYear(newYear);
    setPendingTypeId(newTypeId);
    setPendingDivision(newDivision);

    // Persist year to URL
    updateQuery({ page: 1, year: newYear });
  };

  // Handle filter clear
  const handleClearFilters = () => {
    // Reset all filter state including year
    setAppliedYear(thisYear);
    setAppliedTypeId(null);
    setAppliedDivision(null);
    setPendingYear(thisYear);
    setPendingTypeId(null);
    setPendingDivision(null);
    updateQuery({ page: 1, year: thisYear });
  };

  // Handle export
  const handleExport = useCallback(
    async (format) => {
      setShowExportModal(false);

      setToasterData({
        title: `Exporting to ${format}...`,
        message: 'Preparing your export file',
        type: 'info',
        persistent: true,
      });

      try {
        const params = {
          accountId,
          year: appliedYear,
          typeId: appliedTypeId,
          division: appliedDivision,
          search: input.queryString,
        };

        const jobId = await startExport(params, format);

        subscribeToProgress(jobId, {
          onProgress: (status) => {
            if (status.progress && status.recordCount) {
              setToasterData({
                title: `Exporting to ${format}...`,
                message: `Processing ${status.recordCount} annual purchases (${status.progress}%)`,
                type: 'info',
                persistent: true,
              });
            }
          },
          onComplete: async (data) => {
            try {
              const downloadUrl = await getDownloadUrl(jobId);

              setToasterData({
                title: 'Export Ready!',
                message: `${
                  data.recordCount || 0
                } annual purchases exported to ${format}`,
                type: 'success',
                persistent: true,
                action: {
                  text: `Download ${format}`,
                  icon: 'download',
                  onClick: () => {
                    window.open(downloadUrl, '_blank');
                  },
                },
              });
            } catch (error) {
              setToasterData({
                title: 'Download Error',
                message: 'Failed to generate download link. Please try again.',
                type: 'error',
              });
            }
          },
          onError: (error) => {
            let errorMessage =
              'An error occurred while exporting. Please try again.';

            if (error.message?.includes('permission')) {
              errorMessage =
                'You do not have permission to export annual purchases';
            } else if (error.message?.includes('not found')) {
              errorMessage = 'Export not found or has expired';
            }

            setToasterData({
              title: 'Export Failed',
              message: errorMessage,
              type: 'error',
            });
          },
        });
      } catch (error) {
        setToasterData({
          title: 'Export Failed',
          message: 'Unable to start export. Please try again.',
          type: 'error',
        });
      }
    },
    [
      accountId,
      appliedYear,
      appliedTypeId,
      appliedDivision,
      input.queryString,
      startExport,
      subscribeToProgress,
      getDownloadUrl,
    ]
  );

  // Build filter configuration for TableFilter
  // Generate year options (current year -2 to +1)
  const yearOptions = [
    { label: String(thisYear - 2), value: thisYear - 2 },
    { label: String(thisYear - 1), value: thisYear - 1 },
    { label: String(thisYear), value: thisYear },
    { label: String(thisYear + 1), value: thisYear + 1 },
  ];

  const filterConfig = [
    {
      key: 'year',
      label: 'Year',
      type: 'select',
      placeholder: 'Select Year',
      options: yearOptions,
    },
  ];

  // Add membership type filter - show loading if memberships are being fetched
  if (membershipsLoading) {
    filterConfig.push({
      key: 'typeId',
      label: isTicketHoss ? 'Ticket Type' : 'Pass Type',
      type: 'select',
      placeholder: isTicketHoss ? 'All Tickets' : 'All Passes',
      options: [],
      loading: true,
      loadingText: isTicketHoss ? 'Loading tickets...' : 'Loading passes...',
    });
  } else if (memberships?.getAccountAnnualTickets?.length > 0) {
    filterConfig.push({
      key: 'typeId',
      label: isTicketHoss ? 'Ticket Type' : 'Pass Type',
      type: 'select',
      placeholder: isTicketHoss ? 'All Tickets' : 'All Passes',
      options: memberships.getAccountAnnualTickets.map((ticket) => ({
        value: ticket.id,
        label: ticket.name,
      })),
    });
  }

  // Add division filter if a type is selected
  if (pendingTypeId) {
    if (divisionsLoading) {
      // Show loading state while divisions are being fetched
      filterConfig.push({
        key: 'division',
        label: 'Division',
        type: 'select',
        placeholder: 'All Divisions',
        options: [],
        loading: true,
        loadingText: 'Loading divisions...',
      });
    } else if (
      divisions?.getMembershipDivisions &&
      divisions.getMembershipDivisions.length > 0
    ) {
      filterConfig.push({
        key: 'division',
        label: 'Division',
        type: 'select',
        placeholder: 'All Divisions',
        options: divisions.getMembershipDivisions.map((div) => ({
          value: div,
          label: div,
        })),
      });
    }
  }

  // Current filter values for TableFilter (use pending state for display in filter popup)
  const filterValues = {
    year: pendingYear || '',
    typeId: pendingTypeId || '',
    division: pendingDivision || '',
  };

  // Check if any results have waivers
  const hasWaivers = members.some(
    (member) => member.waivers && member.waivers.length > 0
  );

  // DataTable columns
  const columns = [
    {
      field: 'approved',
      header: 'Approved',
      render: (member) => (
        <Toggle
          checked={!!member.approved}
          onChange={() => handleApprovalToggle(member)}
        />
      ),
    },
    {
      field: 'name',
      header: 'Name',
      mobileTitle: true,
      render: (member) =>
        isTicketHoss ? (
          <CellPrimary>{member.user?.formatted_name}</CellPrimary>
        ) : (
          <MemberLink to={`${routePrefix}/annualsDetail/member/${member.id}`}>
            <CellPrimary>{member.user?.formatted_name}</CellPrimary>
          </MemberLink>
        ),
    },
    {
      field: 'email',
      header: 'Email',
      render: (member) => member.user?.email || '-',
    },
    {
      field: 'phone',
      header: 'Phone',
      render: (member) =>
        member.user?.cellphone ? formatPhoneNumber(member.user.cellphone) : '-',
    },
    {
      field: 'serial_number',
      header: 'Annual #',
      mobileSubtitle: true,
    },
    {
      field: 'membership',
      header: isTicketHoss ? 'Ticket Name' : 'Pass Name',
      render: (member) => member.membership?.name || '-',
    },
  ];

  // Add agreements column if waivers exist
  if (hasWaivers) {
    columns.push({
      field: 'is_signed',
      header: 'Agreements',
      render: (member) =>
        member.is_signed ? (
          <Badge variant="success" size="small">
            Signed
          </Badge>
        ) : (
          <WaiverNotSigned width={30} style={{ padding: 2 }} />
        ),
    });
  }

  // DataTable actions
  const actions = isTicketHoss
    ? []
    : [
        {
          type: 'view',
          title: 'View Details',
          mobileLabel: 'View',
          to: (member) => `${routePrefix}/annualsDetail/member/${member.id}`,
        },
      ];

  const totalPages = Math.ceil(count / 15);

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="users" />
            <div>
              <HeaderTitle>Annual Purchases</HeaderTitle>
              <HeaderSubtitle>
                {accountName || 'Account'} - {appliedYear}
              </HeaderSubtitle>
            </div>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Content Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Page Header with Search and Filters */}
                <PageToolbar>
                  <SearchInput
                    onSearch={handleSearch}
                    placeholder="Search by user name..."
                    debounceMs={500}
                  />
                  <TableFilter
                    filters={filterConfig}
                    values={filterValues}
                    onChange={handleFilterChange}
                    onApply={handleApplyFilters}
                    onClear={handleClearFilters}
                  />
                </PageToolbar>

                {/* DataTable */}
                <DataTable
                  columns={columns}
                  data={members}
                  loading={loading}
                  keyField="id"
                  emptyMessage="No annual purchases found"
                  emptySubtext="Try adjusting your search or filters"
                  actions={actions}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            {/* Quick Actions */}
            <QuickActions
              title="Quick Actions"
              actions={[
                {
                  label: 'Export Report',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                      <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                    </svg>
                  ),
                  onClick: () => setShowExportModal(true),
                },
              ]}
            />
          </Sidebar>
        </DashboardLayout>

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={isExporting}
          recordCount={count}
          dataType="annual purchases"
        />

        {/* Approve Modal */}
        <ApproveModal
          itemType="Member"
          isVisible={showApproveModal}
          onConfirm={onConfirmApprove}
          setIsVisible={setShowApproveModal}
          unCheck={unCheck}
        />

        {/* Toaster */}
        {toasterData && (
          <Toaster
            isOpen={!!toasterData}
            onClose={() => setToasterData(null)}
            title={toasterData.title}
            message={toasterData.message}
            type={toasterData.type}
            duration={toasterData.persistent ? 25000 : 10000}
            persistent={toasterData.persistent}
            action={toasterData.action}
          />
        )}
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackAnnualUsers;
